/**
 * @fileoverview View mode content for patient details modal
 */
import React from "react";
import { formatDate, formatCpf, formatCep } from '../../utils/formatters';
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { Box, Grid, Chip, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import dayjs from "dayjs";
import { DmsInfoCard } from "../DmsDetailModal/DmsInfoCard";
import { DmsInfoField } from "../DmsDetailModal/DmsInfoField";
import { PatientStatusEnum, PatientStatus } from "../../constants/PatientStatusEnum";
import { PatientStatusReason } from "../../constants/PatientStatusReason";

/**
 * View mode content for displaying patient details
 * Receives `data` prop from DmsDetailModal
 */
export const ViewModeContent = ({ data }) => {
  const patient = data;

  const getStatusChip = () => {
    return <Chip label={PatientStatus.getText(patient.status)} color={PatientStatus.getColor(patient.status)} size="small" />;
  };

  const getGenderLabel = (gender) => {
    if (gender === "M") return intl.get("Male").d("Male");
    if (gender === "F") return intl.get("Female").d("Female");
    return gender;
  };

  // Render methods as separate lines
  const renderMethods = (methods) => {
    if (!methods || methods.length === 0) {
      return <Typography variant="body1">-</Typography>;
    }
    return (
      <Box>
        {methods.map((method, index) => (
          <Typography key={method.pk || index} variant="body1" sx={{ mb: 0.5 }}>
            • {method.reason}
          </Typography>
        ))}
      </Box>
    );
  };


  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
      {/* Basic Information */}
      <DmsInfoCard icon={<PersonIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
        <Grid container spacing={2}>
          <DmsInfoField label={intl.get("PatientID").d("Patient ID")} value={patient.patient_identifier} />
          <DmsInfoField label={intl.get("Name").d("Name")} value={patient.name} />
          <DmsInfoField label={intl.get("CPF").d("CPF")} value={formatCpf(patient.cpf)} />
          <DmsInfoField label={intl.get("RG").d("RG")} value={patient.rg || "-"} />
          <DmsInfoField label={intl.get("RGIssuingAgency").d("Issuing Agency")} value={patient.rg_issuing_agency || "-"} />
          <DmsInfoField label={intl.get("RGIssuingDate").d("RG Issuing Date")} value={formatDate(patient.rg_issuing_date)} />
          <DmsInfoField label={intl.get("BirthDate").d("Birth Date")} value={formatDate(patient.birth_date)} />
          <DmsInfoField label={intl.get("Gender").d("Gender")} value={getGenderLabel(patient.gender)} />
          <Grid item xs={6} sm={4}>
            <Box sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 0.5 }}>
              {intl.get("Status").d("Status")}
            </Box>
            {getStatusChip()}
          </Grid>
          {patient.status === PatientStatusEnum.INACTIVE && patient.patient_status_reason && (
            <DmsInfoField
              label={intl.get("DeactivationReason").d("Deactivation Reason")}
              value={PatientStatusReason.getText(patient.patient_status_reason.name)}
              xs={12}
              sm={8}
            />
          )}
          {patient.status === PatientStatusEnum.INACTIVE && patient.status_reason && (
            <DmsInfoField
              label={intl.get("CustomReason").d("Custom Reason")}
              value={patient.status_reason}
              xs={12}
            />
          )}
          {patient.status === PatientStatusEnum.ACTIVE && patient.status_reason && (
            <DmsInfoField
              label={intl.get("ActivationJustification").d("Activation Justification")}
              value={patient.status_reason}
              xs={12}
            />
          )}
        </Grid>
      </DmsInfoCard>

      {/* Contact Information */}
      <DmsInfoCard icon={<ContactPhoneIcon />} title={intl.get("ContactInfo").d("Contact Information")}>
        <Grid container spacing={2}>
          <DmsInfoField label={intl.get("Email").d("Email")} value={patient.email || "-"} xs={12} sm={6} />
          <DmsInfoField label={intl.get("Phone").d("Phone")} value={patient.phone || "-"} />
          <DmsInfoField label={intl.get("HomePhone").d("Home Phone")} value={patient.home_phone || "-"} />
        </Grid>
      </DmsInfoCard>

      {/* LGPD Consent Information */}
      <DmsInfoCard icon={<VerifiedUserIcon />} title={intl.get("ConsentInformation").d("Informações de Consentimento")}>
        <Grid container spacing={2}>
          <DmsInfoField
            label={intl.get("ConsentAcceptanceDate").d("Data de Aceitação do Consentimento")}
            value={patient.created_on ? dayjs(patient.created_on).format("DD/MM/YYYY HH:mm") : "-"}
            xs={12}
            sm={6}
          />
          <DmsInfoField
            label={intl.get("ContactAuthorization").d("Autoriza Contato")}
            value={
              patient.contact_authorization === true
                ? intl.get("Yes").d("Sim")
                : patient.contact_authorization === false
                ? intl.get("No").d("Não")
                : intl.get("NotProvided").d("Não Informado")
            }
            xs={12}
            sm={6}
          />
        </Grid>
      </DmsInfoCard>

      {/* Medical Information - only for female patients */}
      {patient.gender === "F" && (
        <DmsInfoCard icon={<LocalHospitalIcon />} title={intl.get("MedicalInfo").d("Medical Information")}>
          <Grid container spacing={2}>
            <DmsInfoField
              label={intl.get("HasPregnancyPotential").d("Has Pregnancy Potential")}
              value={patient.has_pregnancy_potential ? intl.get("Yes").d("Yes") : intl.get("No").d("No")}
            />
            {patient.has_pregnancy_potential && (
              <>
                <DmsInfoField
                  label={intl.get("MenstrualCycleRegular").d("Menstrual Cycle Regular")}
                  value={patient.menstrual_cycle_regular ? intl.get("Yes").d("Yes") : intl.get("No").d("No")}
                />
                <Grid item xs={12}>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 0.5 }}>
                    {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")}
                  </Box>
                  {renderMethods(patient.highly_effective_methods)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 0.5 }}>
                    {intl.get("BarrierMethods").d("Barrier Method")}
                  </Box>
                  {renderMethods(patient.barrier_methods)}
                </Grid>
              </>
            )}
            {!patient.has_pregnancy_potential && patient.infertility_reason && (
              <DmsInfoField
                label={intl.get("InfertilityReason").d("Infertility Reason")}
                value={patient.infertility_reason.reason || "-"}
                xs={12}
              />
            )}
          </Grid>
        </DmsInfoCard>
      )}

      {/* Address Information */}
      {patient.address && (
        <DmsInfoCard icon={<HomeIcon />} title={intl.get("AddressInfo").d("Address Information")}>
          <Grid container spacing={2}>
            <DmsInfoField label={intl.get("ZipCode").d("Zip Code")} value={formatCep(patient.address.zip_code) || "-"} xs={4} />
            <DmsInfoField label={intl.get("Address").d("Address")} value={patient.address.address || "-"} xs={8} />
            <DmsInfoField label={intl.get("Number").d("Number")} value={patient.address.number || "-"} xs={4} />
            <DmsInfoField label={intl.get("City").d("City")} value={patient.address.city || "-"} xs={4} />
            <DmsInfoField label={intl.get("State").d("State")} value={patient.address.state || "-"} xs={4} />
          </Grid>
        </DmsInfoCard>
      )}

      {/* Responsible Legal Information */}
      {patient.responsible_legal && (
        <DmsInfoCard
          icon={<SupervisorAccountIcon />}
          title={intl.get("ResponsibleLegal").d("Legal Guardian")}
        >
          <Grid container spacing={2}>
            <DmsInfoField label={intl.get("Name").d("Name")} value={patient.responsible_legal.name} xs={12} sm={6} />
            <DmsInfoField label={intl.get("CPF").d("CPF")} value={formatCpf(patient.responsible_legal.cpf)} />
            <DmsInfoField label={intl.get("RG").d("RG")} value={patient.responsible_legal.rg || "-"} />
            <DmsInfoField label={intl.get("RGIssuingAgency").d("Issuing Agency")} value={patient.responsible_legal.rg_issuing_agency || "-"} />
            <DmsInfoField label={intl.get("RGIssuingDate").d("RG Issuing Date")} value={formatDate(patient.responsible_legal.rg_issuing_date) || "-" } />
            <DmsInfoField label={intl.get("BirthDate").d("Birth Date")} value={formatDate(patient.responsible_legal.birth_date)} />
            <DmsInfoField label={intl.get("Email").d("Email")} value={patient.responsible_legal.email || "-"} xs={12} sm={6} />
            <DmsInfoField label={intl.get("Phone").d("Phone")} value={patient.responsible_legal.phone || "-"} />
          </Grid>
        </DmsInfoCard>
      )}
    </Box>
  );
};

ViewModeContent.propTypes = {
  data: PropTypes.object.isRequired,
};
